import { create } from 'zustand';

import { clientApi, deballer } from '@/shared/api/client';
import { ErreurApi } from '@/shared/api/erreurs';
import {
  definirRafraichisseur,
  ecrireJetonAcces,
  ecrireJetonRafraichissement,
  lireJetonRafraichissement,
  viderJetons,
} from '@/shared/api/jetons';

/** Session telle que l'API la renvoie a la connexion et au rafraichissement. */
interface ReponseSession {
  jetonAcces: string;
  jetonRafraichissement: string;
  expireLe: string;
  utilisateurId: string;
  entrepriseActive?: string | null;
  role?: string | null;
}

/** Roles, ordonnes comme au back : Proprietaire > Membre > LectureSeule. */
export type Role = 'Proprietaire' | 'Membre' | 'LectureSeule';

const RANG: Record<string, number> = {
  Proprietaire: 3,
  Membre: 2,
  LectureSeule: 1,
};

export interface EtatSession {
  /** `null` tant que la restauration au demarrage n'a pas abouti. */
  utilisateurId: string | null;
  entrepriseActive: string | null;
  role: Role | null;

  /** Vrai pendant la tentative de restauration initiale, pour eviter un flash de page de connexion. */
  restaurationEnCours: boolean;

  connecter: (email: string, motDePasse: string, entrepriseId?: string) => Promise<void>;
  inscrire: (donnees: {
    email: string;
    motDePasse: string;
    prenom?: string;
    nom?: string;
  }) => Promise<void>;
  /** Echange le jeton courant contre un jeton lie a l'entreprise demandee. */
  choisirEntreprise: (entrepriseId: string) => Promise<void>;
  deconnecter: () => Promise<void>;
  restaurer: () => Promise<void>;
}

/**
 * Session utilisateur.
 *
 * L'ENTREPRISE ACTIVE FAIT PARTIE DE LA SESSION, elle n'est pas un reglage local.
 * L'API la lit dans le jeton (§5 du back) : en changer suppose donc d'obtenir un
 * NOUVEAU jeton. C'est la raison pour laquelle `choisirEntreprise` passe par le
 * serveur au lieu d'ecrire une variable — et c'est aussi ce qui rend impossible
 * l'affichage des donnees d'une entreprise dont on n'est pas membre : l'API
 * refuserait d'emettre le jeton.
 */
export const useSession = create<EtatSession>()((set, get) => {
  /** Applique une reponse de session : jetons + etat exposes a l'interface. */
  function appliquer(session: ReponseSession): void {
    ecrireJetonAcces(session.jetonAcces);
    ecrireJetonRafraichissement(session.jetonRafraichissement);

    set({
      utilisateurId: session.utilisateurId,
      entrepriseActive: session.entrepriseActive ?? null,
      role: (session.role as Role | null) ?? null,
      restaurationEnCours: false,
    });
  }

  function oublier(): void {
    viderJetons();
    set({
      utilisateurId: null,
      entrepriseActive: null,
      role: null,
      restaurationEnCours: false,
    });
  }

  /**
   * Rafraichit la session. Utilisee a trois moments : restauration au demarrage,
   * changement d'entreprise, et rejeu automatique apres un 401.
   */
  async function rafraichir(entrepriseId?: string): Promise<ReponseSession | null> {
    const jetonRafraichissement = lireJetonRafraichissement();

    if (!jetonRafraichissement) {
      return null;
    }

    try {
      const session = await deballer(
        clientApi.POST('/v1/auth/rafraichir', {
          body: {
            jetonRafraichissement,
            ...(entrepriseId ? { entrepriseId } : {}),
          },
        }),
      );

      appliquer(session as ReponseSession);
      return session as ReponseSession;
    } catch (erreur) {
      // Un refus d'entreprise ne doit PAS deconnecter : l'utilisateur reste
      // valablement connecte, il a seulement demande une entreprise interdite.
      const refusDEntreprise =
        erreur instanceof ErreurApi && erreur.code === 'platform.auth.entreprise_non_autorisee';

      if (!refusDEntreprise) {
        oublier();
      }

      throw erreur;
    }
  }

  // Branche le rafraichissement automatique du client HTTP (voir `jetons.ts`).
  definirRafraichisseur(async () => {
    try {
      const session = await rafraichir(get().entrepriseActive ?? undefined);
      return session?.jetonAcces ?? null;
    } catch {
      return null;
    }
  });

  return {
    utilisateurId: null,
    entrepriseActive: null,
    role: null,
    restaurationEnCours: true,

    async connecter(email, motDePasse, entrepriseId) {
      const session = await deballer(
        clientApi.POST('/v1/auth/connexion', {
          body: { email, motDePasse, ...(entrepriseId ? { entrepriseId } : {}) },
        }),
      );

      appliquer(session as ReponseSession);
    },

    async inscrire(donnees) {
      const session = await deballer(
        clientApi.POST('/v1/auth/inscription', { body: donnees }),
      );

      appliquer(session as ReponseSession);
    },

    async choisirEntreprise(entrepriseId) {
      if (get().entrepriseActive === entrepriseId) {
        return;
      }

      await rafraichir(entrepriseId);
    },

    async deconnecter() {
      try {
        await deballer(clientApi.POST('/v1/auth/deconnexion', {}));
      } catch {
        // Meme si l'API est injoignable, la session locale doit disparaitre :
        // laisser un jeton actif dans le navigateur serait pire que l'echec.
      }

      oublier();
    },

    async restaurer() {
      if (!lireJetonRafraichissement()) {
        set({ restaurationEnCours: false });
        return;
      }

      try {
        await rafraichir();
      } catch {
        // `rafraichir` a deja nettoye : rien a ajouter.
      }
    },
  };
});

/** Vrai si le role courant atteint au moins le niveau demande. */
export function aAuMoins(role: Role | null, requis: Role): boolean {
  return (RANG[role ?? ''] ?? 0) >= (RANG[requis] ?? 0);
}

/** Hook pratique : `useRole('Membre')` pour conditionner une action d'ecriture. */
export function useRoleAuMoins(requis: Role): boolean {
  const role = useSession((etat) => etat.role);
  return aAuMoins(role, requis);
}
